import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { User, LogOut, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getPatient } from '../../services/patientService'
import { formatSriLankanMobile } from '../../lib/utils'

const ProfilePopover = ({ patient, onLogout, onChangeCustomer, onOpenSignup }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [showPatientModal, setShowPatientModal] = useState(false)
  const [existingPatients, setExistingPatients] = useState([])
  const [loadingPatients, setLoadingPatients] = useState(false)
  const popoverRef = useRef(null)

  const userIdFromEnv = import.meta.env.VITE_USER_ID
  const effectiveUserId = Number.parseInt(userIdFromEnv, 10)
  const effectiveToken = patient?.deviceToken || ''

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleLogout = () => {
    setIsOpen(false)
    onLogout()
  }

  const handleChangeCustomer = async () => {
    setLoadingPatients(true)
    try {
      const formattedPhone = formatSriLankanMobile(patient?.Mobile || '')
      if (!formattedPhone) return
      if (!Number.isFinite(effectiveUserId)) return
      if (!effectiveToken) return

      const response = await getPatient({
        UserId: effectiveUserId,
        Mobile: formattedPhone,
        DateOfBirth: '',
        UniqueId: '',
        token: effectiveToken,
        isDeviceToken: true
      })

      if (response.status === 1000 && response.data && Array.isArray(response.data)) {
        setExistingPatients(response.data)
        setShowPatientModal(true)
      }
    } catch (error) {
      console.error('Failed to load patients:', error)
    } finally {
      setLoadingPatients(false)
    }
  }

  const patientName = patient
    ? [patient.Title, patient.FirstName, patient.LastName]
        .filter(Boolean)
        .join(' ')
    : 'Guest'

  return (
    <div className="relative" ref={popoverRef}>
      {showPatientModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9995] flex items-center justify-center bg-slate-950/70 px-3 py-3 backdrop-blur-md"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowPatientModal(false)
            }}
          >
            <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Select your patient record</p>
                  <p className="text-xs text-slate-500">
                    {existingPatients.length
                      ? `${existingPatients.length} profile(s) found for this mobile`
                      : 'No matching profiles found.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowPatientModal(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="relative z-10 px-5 pb-6 pt-5">
                {existingPatients.length > 0 ? (
                  <div className="space-y-2 max-h-36 overflow-y-auto">
                    {existingPatients.map((p) => (
                      <button
                        key={p.Id}
                        onClick={() => {
                          setShowPatientModal(false)
                          setIsOpen(false)

                          // Keep current deviceToken; switch patient details.
                          onChangeCustomer?.({ ...p, deviceToken: effectiveToken })
                        }}
                        className="w-full text-left p-3 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <p className="font-medium text-slate-900">
                          {p.Title} {p.FirstName} {p.LastName}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          NIC: {p.NIC || 'N/A'} | DOB: {p.DateOfBirth ? new Date(p.DateOfBirth).toLocaleDateString('en-GB') : 'N/A'}
                        </p>
                        {p.UniqueId && (
                          <p className="text-xs text-slate-500">ID: {p.UniqueId}</p>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 text-center border border-dashed border-slate-200 rounded-lg bg-slate-50">
                    <p className="text-sm font-semibold text-slate-900">No patient profiles found</p>
                    <p className="text-xs text-slate-500 mt-1">Create a new patient profile to continue.</p>
                  </div>
                )}

                <div className="pt-3 border-t border-slate-200 mt-3">
                  <button
                    onClick={() => {
                      setShowPatientModal(false)
                      setIsOpen(false)
                      onOpenSignup?.(patient?.Mobile || '')
                    }}
                    className="w-full text-center p-3 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-blue-600 font-medium"
                  >
                    + Create New Profile
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-[#7096F3] via-[#4181F3] to-[#C08EDB] text-white font-semibold">
          {patient?.FirstName?.[0]?.toUpperCase() || 'U'}
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-[10000]">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-[#7096F3] via-[#4181F3] to-[#C08EDB] text-white font-bold text-lg">
                {patient?.FirstName?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  {patientName}
                </p>
                {patient?.Mobile && (
                  <p className="text-sm text-gray-500 truncate">
                    {patient.Mobile}
                  </p>
                )}
              </div>
            </div>
            {patient?.UniqueId && (
              <p className="mt-2 text-xs text-gray-500">
                ID: {patient.UniqueId}
              </p>
            )}
          </div>

          <div className="p-2">
            <button
              onClick={handleChangeCustomer}
              disabled={loadingPatients}
              className="w-full flex items-center gap-3 px-4 py-2 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Users size={18} />
              <span className="font-medium">{t('Change Customer') || 'changeCustomer'}</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-left text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              <span className="font-medium">{t('logout') || 'Logout'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfilePopover
